/**
 * Navigation Configuration
 *
 * Single source of truth for all navigation menu items.
 * Supports multiple workspaces (Creator Hub, Operations).
 *
 * @module NavigationConfig
 */

import {
  IconUsers,
  IconBuildingStore,
  IconSpeakerphone,
  IconClipboardList,
  IconUserCheck,
  IconSettings,
  IconCoin,
  IconHome,
  IconRocket,
  IconGauge,
  IconFileInvoice,
  IconCurrencyDollar,
  IconTarget,
  IconChartFunnel,
  IconFileText,
  IconChartBar,
  IconMessageCircle,
  IconSend,
  IconPlus,
  IconChecklist,
  IconActivity,
  IconRadar,
  IconRobot
} from '@tabler/icons-react';

/**
 * Menu item structure
 * @typedef {Object} MenuItem
 * @property {string} id - Unique identifier
 * @property {string} label - Display label
 * @property {React.Component} icon - Icon component from @tabler/icons-react
 * @property {string} [path] - Route path (optional for items with submenu)
 * @property {boolean} [hasSubmenu] - Whether item has submenu
 * @property {MenuItem[]} [submenu] - Submenu items
 * @property {number} order - Display order (for sorting)
 * @property {string|string[]} [permission] - One or more accepted legacy permissions
 * @property {boolean} [external] - Whether the destination leaves the internal SPA
 * @property {Object} [access] - Operations page/API access contract
 * @property {string[]} [roles] - Any accepted user role
 * @property {string[]} [aliases] - Search and compatibility terms
 * @property {number} [mobilePriority] - Mobile ordering priority
 * @property {boolean} [mobilePinned] - Whether the destination is pinned on phones
 * @property {string[]} [breadcrumb] - Breadcrumb labels for this destination
 * @property {boolean} [commandPalette] - Whether the destination is searchable
 */

const createOpsPageContract = ({
  id,
  path,
  requiredPermissions,
  primaryApis,
  optionalCapabilities = [],
  admissionOnly = false,
}) => Object.freeze({
  id,
  path,
  overridePermission: 'ops_access',
  requiredPermissions: Object.freeze(requiredPermissions),
  primaryApis: Object.freeze(primaryApis.map(api => Object.freeze(api))),
  optionalCapabilities: Object.freeze(optionalCapabilities.map(capability => Object.freeze(capability))),
  admissionOnly,
});

/**
 * Canonical Operations route-to-API contract.
 *
 * `requiredPermissions` are all required unless `ops_access` is present. Each
 * API entry records the permission enforced by the backend route middleware;
 * null means the endpoint only requires authentication.
 */
export const OPS_PAGE_CONTRACTS = Object.freeze({
  'ops-campaigns': createOpsPageContract({
    id: 'ops-campaigns',
    path: '/ops/campaigns',
    requiredPermissions: ['ops_campaigns'],
    primaryApis: [{ path: '/ops/campaigns', permission: 'ops_campaigns' }],
    optionalCapabilities: [
      { id: 'campaign-invoices', path: '/ops/invoices', permission: 'ops_invoices' },
    ],
  }),
  'ops-invoices': createOpsPageContract({
    id: 'ops-invoices',
    path: '/ops/invoices',
    requiredPermissions: ['ops_invoices'],
    // Browse-only since Revision 8: invoices are created in the Sales CRM, so the page no
    // longer loads brands or the campaign picker.
    primaryApis: [{ path: '/ops/invoices', permission: 'ops_invoices' }],
  }),
  'ops-transactions': createOpsPageContract({
    id: 'ops-transactions',
    path: '/ops/transactions',
    requiredPermissions: ['ops_transactions'],
    primaryApis: [{ path: '/ops/transactions', permission: 'ops_transactions' }],
  }),
  'ops-sales': createOpsPageContract({
    id: 'ops-sales',
    path: '/ops/sales',
    requiredPermissions: ['ops_sales'],
    primaryApis: [{ path: '/ops/sales-leads', permission: 'ops_sales' }],
  }),
  'ops-bd': createOpsPageContract({
    id: 'ops-bd',
    path: '/ops/bd-pipeline',
    requiredPermissions: ['ops_sales'],
    primaryApis: [{ path: '/ops/sales-leads', permission: 'ops_sales' }],
  }),
  'ops-brands': createOpsPageContract({
    id: 'ops-brands',
    path: '/ops/brands',
    requiredPermissions: ['ops_brands'],
    primaryApis: [{ path: '/brands/with-revenue', permission: 'ops_brands' }],
  }),
  'ops-proposals': createOpsPageContract({
    id: 'ops-proposals',
    path: '/ops/proposals',
    requiredPermissions: ['ops_proposals'],
    primaryApis: [{ path: '/ops/proposals', permission: 'ops_proposals' }],
  }),
  'ops-feedback': createOpsPageContract({
    id: 'ops-feedback',
    path: '/ops/feedback',
    requiredPermissions: ['ops_feedback'],
    primaryApis: [{ path: '/ops/feedback', permission: 'ops_feedback' }],
  }),
});

export const hasOpsPageAccess = (contract, hasPermission = () => false) => (
  Boolean(contract) && (
    hasPermission(contract.overridePermission)
    || contract.requiredPermissions.every(permission => hasPermission(permission))
  )
);

export const USER_PERMISSION_GROUPS = Object.freeze([
  Object.freeze({
    id: 'core',
    label: 'Core workspace',
    permissions: Object.freeze([
      { key: 'manage_campaigns', label: 'Manage campaigns' },
      { key: 'manage_creators', label: 'Manage creators' },
      { key: 'manage_payments', label: 'Manage payments' },
      { key: 'manage_messaging', label: 'Manage messaging' },
      { key: 'manage_briefs', label: 'Manage briefs' },
      { key: 'view_analytics', label: 'View analytics' },
      { key: 'manage_settings', label: 'Manage settings' }
    ].map(Object.freeze))
  }),
  Object.freeze({
    id: 'operations',
    label: 'Operations',
    permissions: Object.freeze([
      { key: 'ops_access', label: 'Full Operations access' },
      { key: 'ops_campaigns', label: 'Campaigns' },
      { key: 'ops_invoices', label: 'Invoices' },
      { key: 'ops_transactions', label: 'Transactions' },
      { key: 'ops_sales', label: 'Sales CRM & BD pipeline' },
      { key: 'ops_brands', label: 'Brands' },
      { key: 'ops_proposals', label: 'Proposals' },
      { key: 'ops_feedback', label: 'Feedback' }
    ].map(Object.freeze))
  })
]);

/**
 * Workspace definitions with their menu items
 */
const NAVIGATION_SOURCE_GROUPS = Object.freeze({
  creatorHub: {
    id: 'creatorHub',
    label: 'Creator Hub',
    icon: IconUsers,
    items: Object.freeze([
      {
        id: 'home',
        label: 'Home',
        icon: IconHome,
        path: '/',
        order: 0
      },
      {
        id: 'creators',
        label: 'Creators',
        icon: IconUsers,
        path: '/creators',
        order: 1
      },
      {
        id: 'pending-creators',
        label: 'Waiting for approval',
        icon: IconUserCheck,
        path: '/creators/pending',
        permission: 'manage_creators',
        badge: 'pendingCreators',
        order: 1
      },
      {
        id: 'creator-notes',
        label: 'Notes focus',
        icon: IconFileText,
        path: '/creator-notes',
        permission: 'manage_creators',
        order: 1
      },
      {
        id: 'creator-selection',
        label: 'Creator selection',
        icon: IconUserCheck,
        path: '/creator-selection',
        permission: ['manage_creators', 'manage_campaigns'],
        order: 1
      },
      {
        id: 'launch-scout',
        label: 'Launch Scout',
        icon: IconRadar,
        path: '/launch-scout',
        roles: ['admin'],
        order: 1
      },
      {
        id: 'creator-analytics',
        label: 'Creator Analytics',
        icon: IconChartBar,
        path: '/creator-analytics',
        order: 1
      },
      {
        id: 'workload-dashboard',
        label: 'Team Workload',
        icon: IconUserCheck,
        path: '/workload-dashboard',
        order: 2
      },
      {
        id: 'brands',
        label: 'Brands',
        icon: IconBuildingStore,
        path: '/brands',
        order: 3
      },
      {
        id: 'campaigns',
        label: 'Campaigns',
        icon: IconSpeakerphone,
        path: '/campaigns',
        order: 4
      },
      {
        id: 'takeover',
        label: 'Timeline Takeover',
        icon: IconRocket,
        path: '/takeover',
        roles: ['admin'],
        order: 5
      },
      // Same access as Campaigns: every signed-in team member (no permission, no role).
      {
        id: 'launches',
        label: 'Launches',
        icon: IconActivity,
        path: '/launches',
        order: 6
      },
      {
        id: 'content-review',
        label: 'Content Review',
        icon: IconClipboardList,
        path: '/content-review',
        order: 7
      },
      { id: 'briefs', label: 'Briefs', icon: IconFileText, path: '/briefs', order: 7 },
      {
        id: 'payments',
        label: 'Payments',
        icon: IconCoin,
        path: '/payments',
        hasSubmenu: true,
        order: 9,
        submenu: [
          {
            id: 'payments-dashboard',
            label: 'Dashboard',
            path: '/payments',
            permission: 'manage_payments',
            order: 0
          },
          {
            id: 'payments-history',
            label: 'Payment History',
            path: '/payments/history',
            permission: 'manage_payments',
            order: 1
          }
        ]
      },
      // AI tools (platform MCP setup + connected apps) is open to every logged-in user.
      {
        id: 'ai-tools',
        label: 'AI tools',
        icon: IconRobot,
        path: '/ai-tools',
        order: 10
      },
      {
        id: 'settings',
        label: 'Admin settings',
        icon: IconSettings,
        path: '/settings',
        hasSubmenu: true,
        order: 10,
        roles: ['admin'],
        submenu: [
          {
            id: 'settings-main',
            label: 'Admin settings',
            path: '/settings',
            order: 0
          },
          {
            id: 'settings-users',
            label: 'User Management',
            path: '/users',
            order: 1,
            roles: ['admin']
          }
        ]
      }
    ])
  },
  operations: {
    id: 'operations',
    label: 'Operations',
    icon: IconGauge,
    items: Object.freeze([
      // Team tasks is open to every logged-in user (readers are read-only):
      // it deliberately has no `access` contract and no `permission`.
      {
        id: 'team-tasks',
        label: 'Team tasks',
        icon: IconChecklist,
        path: '/ops/tasks',
        order: 0
      },
      {
        id: 'ops-campaigns',
        label: 'Campaigns',
        icon: IconSpeakerphone,
        path: '/ops/campaigns',
        order: 1,
        access: OPS_PAGE_CONTRACTS['ops-campaigns']
      },
      {
        id: 'ops-invoices',
        label: 'Invoices',
        icon: IconFileInvoice,
        path: '/ops/invoices',
        order: 4,
        access: OPS_PAGE_CONTRACTS['ops-invoices']
      },
      {
        id: 'ops-transactions',
        label: 'Transactions',
        icon: IconCurrencyDollar,
        path: '/ops/transactions',
        order: 5,
        access: OPS_PAGE_CONTRACTS['ops-transactions']
      },
      {
        id: 'ops-sales',
        label: 'Sales CRM',
        icon: IconTarget,
        path: '/ops/sales',
        order: 6,
        access: OPS_PAGE_CONTRACTS['ops-sales']
      },
      {
        id: 'ops-bd',
        label: 'BD Pipeline',
        icon: IconChartFunnel,
        path: '/ops/bd-pipeline',
        order: 7,
        access: OPS_PAGE_CONTRACTS['ops-bd']
      },
      {
        id: 'outreach',
        label: 'Outreach',
        icon: IconSend,
        path: 'https://leads.wetakeover.xyz',
        external: true,
        permission: ['ops_access', 'ops_sales'],
        order: 8
      },
      {
        id: 'ops-brands',
        label: 'Brands',
        icon: IconBuildingStore,
        path: '/ops/brands',
        order: 9,
        access: OPS_PAGE_CONTRACTS['ops-brands']
      },
      {
        id: 'ops-proposals',
        label: 'Proposals',
        icon: IconFileText,
        path: '/ops/proposals',
        order: 10,
        access: OPS_PAGE_CONTRACTS['ops-proposals']
      },
      {
        id: 'ops-feedback',
        label: 'Feedback',
        icon: IconMessageCircle,
        path: '/ops/feedback',
        order: 12,
        access: OPS_PAGE_CONTRACTS['ops-feedback']
      }
    ])
  }
});

const ITEM_METADATA = Object.freeze({
  home: { aliases: ['dashboard', 'control panel'], mobilePriority: 1, mobilePinned: true },
  creators: { aliases: ['creator management', 'creator notes'], mobilePriority: 2, mobilePinned: true },
  'pending-creators': { aliases: ['pending creators', 'approve creators', 'creator approval', 'new sign ups'], mobilePriority: 18 },
  'creator-notes': { aliases: ['notes focus', 'review creator notes'], mobilePriority: 19 },
  'launch-scout': { aliases: ['launch scout', 'creator discovery', 'jev'], mobilePriority: 24 },
  'creator-analytics': { aliases: ['analytics'], mobilePriority: 20 },
  'workload-dashboard': { aliases: ['workload', 'availability'], mobilePriority: 21 },
  brands: { aliases: ['brand management'], mobilePriority: 22 },
  campaigns: { aliases: ['workflow', 'campaign workflow'], mobilePriority: 3, mobilePinned: true },
  launches: { aliases: ['launch dashboard', 'campaign launch', 'launch tracker', 'live impressions'], mobilePriority: 9 },
  briefs: { aliases: ['campaign briefs', 'brief ready'], mobilePriority: 10 },
  takeover: { aliases: ['timeline takeover'], mobilePriority: 23 },
  'content-review': { aliases: ['review', 'content'], mobilePriority: 11 },
  payments: { aliases: ['finance', 'wallet'], mobilePriority: 13 },
  'payments-dashboard': { aliases: ['payments', 'finance'], commandLabel: 'Payments' },
  'payments-history': { aliases: ['payment history', 'transactions'] },
  settings: { aliases: ['preferences'], mobilePriority: 30 },
  'settings-main': { aliases: ['settings', 'preferences'], commandLabel: 'Settings' },
  'settings-users': { aliases: ['users', 'user management'] },
  'team-tasks': { aliases: ['tasks', 'task tracker', 'who is working on what'], mobilePriority: 4 },
  'ai-tools': { aliases: ['mcp', 'codex', 'grok', 'claude', 'connected apps', 'use in your ai tool'], mobilePriority: 31 },
  'ops-campaigns': { aliases: ['operations campaigns'], mobilePriority: 41 },
  'ops-invoices': { aliases: ['operations invoices'], mobilePriority: 44 },
  'ops-transactions': { aliases: ['operations transactions'], mobilePriority: 45 },
  'ops-sales': { aliases: ['sales', 'crm'], mobilePriority: 46 },
  'ops-bd': { aliases: ['business development'], mobilePriority: 47 },
  outreach: { aliases: ['leads', 'partner outreach'], mobilePriority: 48 },
  'ops-brands': { aliases: ['operations brands', 'partners'], mobilePriority: 49 },
  'ops-proposals': { aliases: ['operations proposals'], mobilePriority: 50 },
  'ops-feedback': { aliases: ['operations feedback'], mobilePriority: 52 }
});

const enrichItem = (item, group) => {
  const metadata = ITEM_METADATA[item.id] || {};
  return Object.freeze({
    aliases: [],
    mobilePriority: 100,
    mobilePinned: false,
    commandPalette: true,
    breadcrumb: [group.label, item.label],
    ...item,
    ...metadata,
    group: group.id,
    groupLabel: group.label,
    submenu: item.submenu?.map(subItem => enrichItem(subItem, group))
  });
};

/**
 * Canonical, data-driven navigation groups consumed by every active shell surface.
 * The source groups keep permission and breadcrumb metadata organized without
 * exposing the retired workspace switcher in the product shell.
 */
export const NAVIGATION_GROUPS = Object.freeze(
  Object.values(NAVIGATION_SOURCE_GROUPS).map(group => Object.freeze({
    id: group.id,
    label: group.label,
    icon: group.icon,
    items: Object.freeze(group.items.map(item => enrichItem(item, group)))
  }))
);

export const NAVIGATION_ITEMS = Object.freeze(
  NAVIGATION_GROUPS.flatMap(group => group.items)
);

/**
 * Compact task areas used by the visible shell.
 *
 * The canonical leaf registry above remains the source of truth for routes,
 * permissions, breadcrumbs, mobile metadata, and command-palette search. The
 * shell only changes how those leaves are presented: compact task areas in
 * the rail and the current area's destinations in a contextual bar.
 */
export const NAVIGATION_AREAS = Object.freeze([
  Object.freeze({
    id: 'overview',
    label: 'Overview',
    icon: IconHome,
    itemIds: Object.freeze(['home']),
    labels: Object.freeze({})
  }),
  Object.freeze({
    id: 'creators',
    label: 'Creators',
    icon: IconUsers,
    itemIds: Object.freeze(['creators', 'pending-creators', 'creator-notes', 'creator-selection', 'creator-analytics', 'workload-dashboard']),
    labels: Object.freeze({ creators: 'Directory', 'creator-analytics': 'Analytics', 'workload-dashboard': 'Team workload' })
  }),
  Object.freeze({
    id: 'brands',
    label: 'Brands',
    icon: IconBuildingStore,
    itemIds: Object.freeze(['brands', 'ops-brands']),
    labels: Object.freeze({ brands: 'Brand setup', 'ops-brands': 'Brand performance' })
  }),
  Object.freeze({
    id: 'campaign-work',
    label: 'Campaigns',
    icon: IconSpeakerphone,
    itemIds: Object.freeze(['campaigns', 'takeover', 'launches']),
    labels: Object.freeze({})
  }),
  Object.freeze({
    id: 'content',
    label: 'Content',
    icon: IconClipboardList,
    itemIds: Object.freeze(['briefs', 'content-review']),
    labels: Object.freeze({ 'content-review': 'Review' })
  }),
  Object.freeze({
    id: 'growth',
    label: 'Growth',
    icon: IconTarget,
    itemIds: Object.freeze(['ops-sales', 'ops-bd', 'ops-proposals', 'outreach']),
    labels: Object.freeze({
      'ops-bd': 'BD pipeline'
    })
  }),
  Object.freeze({
    id: 'finance',
    label: 'Finance',
    icon: IconCoin,
    itemIds: Object.freeze(['payments-dashboard', 'payments-history', 'ops-campaigns', 'ops-invoices', 'ops-transactions']),
    labels: Object.freeze({
      'payments-dashboard': 'Payments',
      'payments-history': 'Payment history',
      'ops-campaigns': 'Campaign financials'
    })
  }),
  // Its own always-permitted area, so the tracker is a sidebar and bottom-nav entry for everyone.
  Object.freeze({
    id: 'team',
    label: 'Team tasks',
    icon: IconChecklist,
    itemIds: Object.freeze(['team-tasks']),
    labels: Object.freeze({})
  })
]);

export const NAVIGATION_UTILITY_IDS = Object.freeze([
  'ops-feedback',
  'ai-tools',
  'settings-main'
]);

// Global creation stays inside the command palette so the shell remains calm.
// Campaigns also keeps its familiar on-page New Campaign action.
export const COMMAND_ACTIONS = Object.freeze([
  Object.freeze({
    id: 'create-campaign',
    label: 'Create campaign',
    icon: IconPlus,
    path: '/campaigns?action=create',
    permission: 'manage_campaigns',
    aliases: Object.freeze(['new campaign', 'add campaign']),
    group: 'actions',
    groupLabel: 'Actions'
  })
]);

const hasItemPermission = (permission, hasPermission) => (
  !permission || (
    Array.isArray(permission)
      ? permission.some(candidate => hasPermission(candidate))
      : hasPermission(permission)
  )
);

const isPermitted = (item, hasPermission, user) => (
  (item.access ? hasOpsPageAccess(item.access, hasPermission) : hasItemPermission(item.permission, hasPermission)) &&
  (!item.roles || item.roles.includes(user?.role))
);

export const filterPermittedNavigationItem = (
  item,
  hasPermission = () => true,
  user = null
) => {
  if (!isPermitted(item, hasPermission, user)) return null;

  return {
    ...item,
    submenu: item.submenu
      ?.map(subItem => filterPermittedNavigationItem(subItem, hasPermission, user))
      .filter(Boolean)
  };
};

export const getPermittedNavigationGroups = (hasPermission = () => true, user = null) => (
  NAVIGATION_GROUPS
    .map(group => ({
      ...group,
      items: group.items
        .map(item => filterPermittedNavigationItem(item, hasPermission, user))
        .filter(Boolean)
    }))
    .filter(group => group.items.length > 0)
);

const flattenPermittedNavigationLeaves = groups => groups.flatMap(group => (
  group.items.flatMap(item => (
    item.submenu?.length
      ? item.submenu.map(subItem => ({ ...subItem, icon: subItem.icon || item.icon }))
      : [item]
  ))
));

const getPermittedNavigationLeafMap = (hasPermission, user) => new Map(
  flattenPermittedNavigationLeaves(getPermittedNavigationGroups(hasPermission, user))
    .map(item => [item.id, item])
);

export const getPermittedNavigationAreas = (hasPermission = () => true, user = null) => {
  const permittedLeaves = getPermittedNavigationLeafMap(hasPermission, user);

  return NAVIGATION_AREAS
    .map(area => {
      const items = area.itemIds
        .map(id => permittedLeaves.get(id))
        .filter(Boolean)
        .map(item => ({
          ...item,
          label: area.labels[item.id] || item.label,
          areaId: area.id,
          areaLabel: area.label
        }));

      return {
        id: area.id,
        label: area.label,
        icon: area.icon,
        path: items.find(item => !item.external)?.path,
        items
      };
    })
    .filter(area => area.items.length > 0 && area.path);
};

export const getPermittedNavigationUtilities = (hasPermission = () => true, user = null) => {
  const permittedLeaves = getPermittedNavigationLeafMap(hasPermission, user);
  const labels = {
    'ops-feedback': 'Feedback',
    'settings-main': 'Admin settings'
  };

  return NAVIGATION_UTILITY_IDS
    .map(id => permittedLeaves.get(id))
    .filter(Boolean)
    .map(item => ({ ...item, label: labels[item.id] || item.label }));
};

export const getActiveNavigationArea = (pathname, areas) => {
  const activeId = getActiveNavigationState(pathname).activeId;
  const candidates = areas || NAVIGATION_AREAS.map(area => ({
    ...area,
    items: area.itemIds.map(id => ({ id }))
  }));

  return candidates.find(area => area.items.some(item => item.id === activeId)) || null;
};

export const getSearchableNavigationItems = (hasPermission = () => true, user = null) => (
  getPermittedNavigationGroups(hasPermission, user).flatMap(group => (
    group.items.flatMap(item => {
      const candidates = item.submenu?.length
        ? item.submenu.map(subItem => ({ ...subItem, icon: subItem.icon || item.icon }))
        : [item];
      return candidates.filter(candidate => candidate.path && candidate.commandPalette !== false);
    })
  ))
);

export const getPermittedCommandActions = (hasPermission = () => true, user = null) => (
  COMMAND_ACTIONS.filter(action => isPermitted(action, hasPermission, user))
);

const getNavigationLeaves = () => NAVIGATION_ITEMS.flatMap(item => (
  item.submenu?.length
    ? item.submenu.map(subItem => ({ ...subItem, ancestorId: item.id }))
    : [{ ...item, ancestorId: null }]
));

const pathMatches = (path, pathname) => (
  !/^https?:\/\//i.test(path) && (path === '/'
    ? pathname === '/'
    : pathname === path || pathname.startsWith(`${path}/`))
);

/**
 * Resolve one active leaf and, when applicable, its submenu ancestor.
 * Longest-path matching prevents siblings such as /ops and /ops/campaigns,
 * or /payments and /payments/history, from being active together.
 */
export const getActiveNavigationState = (pathname) => {
  const activeItem = getNavigationLeaves()
    .filter(item => item.path && pathMatches(item.path, pathname))
    .sort((a, b) => b.path.length - a.path.length)[0];

  return {
    activeId: activeItem?.id || null,
    ancestorId: activeItem?.ancestorId || null
  };
};

export const isNavigationItemActive = (item, pathname) => (
  getActiveNavigationState(pathname).activeId === item.id
);

export const COMPATIBILITY_REDIRECTS = Object.freeze([
  { from: '/workflow', to: '/campaigns' },
  { from: '/campaigns/:campaignId/delivery', to: '..' },
  { from: '/ops', to: '/' },
  { from: '/ops/delivery', to: '/campaigns' },
  { from: '/messages/*', to: '/campaigns' },
  { from: '/discord-threads/*', to: '/campaigns' },
  { from: '/nudge', to: '/campaigns' },
  { from: '/ops/reports', to: '/campaigns' },
  { from: '/creator-availability', to: '/workload-dashboard' },
  { from: '/ops/partners', to: '/ops/brands' }
]);

/**
 * Mobile breakpoint in pixels
 * Sidebar switches to drawer mode below this width
 */
export const MOBILE_BREAKPOINT = 900;

/**
 * Sidebar width in pixels (desktop)
 */
export const SIDEBAR_WIDTH = 256;

/**
 * Animation duration in milliseconds
 */
export const ANIMATION_DURATION = 0;

/**
 * Search debounce time in milliseconds
 */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Z-index layers for navigation components
 */
export const Z_INDEX = Object.freeze({
  BACKDROP: 40,
  DRAWER: 50,
  DROPDOWN: 60
});
